import { useQuery } from '@apollo/client';
import { GetLatestRiskAssessmentResultConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { z } from 'zod';

import type { ConfigType } from './types';

const ratingSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  value: z.number(),
  color: z.string(),
});

export const riskAssessmentResultConfigSchema = z.object({
  likelihood: z.object({
    ratings: z.array(ratingSchema),
  }),
  impact: z.object({
    ratings: z.array(ratingSchema),
    categories: z.array(z.object({ name: z.string(), color: z.string() })),
    aggregation: z.enum(['average', 'maximum']).optional(),
  }),
  matrix: z.array(
    z.object({
      title: z.string(),
      value: z.number(),
      color: z.string(),
      likelihood: z.number(),
      impact: z.number(),
    })
  ),
});

export type RiskAssessmentResultConfig = z.infer<
  typeof riskAssessmentResultConfigSchema
>;

export const useGetLatestRiskAssessmentResultConfig = (options: {
  type: ConfigType;
  skip?: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery(
    GetLatestRiskAssessmentResultConfigDocument,
    { variables: { Type: options.type }, skip: options.skip }
  );

  const record = data?.risk_assessment_result_config[0];
  const config = record?.Config
    ? riskAssessmentResultConfigSchema.parse(record.Config)
    : undefined;

  return {
    config,
    loading,
    error,
    id: record?.Id,
    originalTimestamp: record?.ModifiedAtTimestamp,
    refetch,
  };
};
